<script lang="ts">
	import { onMount } from 'svelte';
	import type { TocItem } from '$lib/docs/types';
	
	interface Props {
		items: TocItem[];
	}
	
	let { items }: Props = $props();
	
	let activeAnchor = $state(items[0]?.anchor || '');
	let tocElement: HTMLElement;
	let visibleSections = new Set<string>();
	
	// Track scroll position to highlight active section with improved logic
	onMount(() => {
		// Initialize with first item if no scroll has happened
		if (!activeAnchor && items.length > 0) {
			activeAnchor = items[0].anchor;
		}
		
		// Create intersection observer for visibility tracking
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						visibleSections.add(entry.target.id);
					} else {
						visibleSections.delete(entry.target.id);
					}
				});
				
				// Update active anchor based on visible sections
				updateActiveAnchor();
			},
			{
				rootMargin: '-80px 0px -80% 0px',
				threshold: [0]
			}
		);
		
		// Update active anchor based on scroll position
		function updateActiveAnchor() {
			// If we're at the top of the page, highlight the first item
			if (window.scrollY < 100) {
				if (items.length > 0) {
					activeAnchor = items[0].anchor;
				}
				return;
			}
			
			// Find the topmost visible section
			let currentActiveAnchor = activeAnchor;
			const offset = 120; // Account for fixed header + some padding
			
			// Check all items from bottom to top to find the last one that's scrolled past
			for (let i = items.length - 1; i >= 0; i--) {
				const item = items[i];
				const element = document.getElementById(item.anchor);
				if (element) {
					const rect = element.getBoundingClientRect();
					// Check if this heading has been scrolled past
					if (rect.top <= offset) {
						currentActiveAnchor = item.anchor;
						break;
					}
				} else {
					console.warn(`Element with anchor '${item.anchor}' not found`);
				}
			}
			
			if (currentActiveAnchor !== activeAnchor) {
				activeAnchor = currentActiveAnchor;
			}
		}
		
		// Handle scroll events for smoother tracking
		function handleScroll() {
			requestAnimationFrame(() => {
				updateActiveAnchor();
			});
		}
		
		// Observe all headings
		items.forEach((item) => {
			const element = document.getElementById(item.anchor);
			if (element) {
				observer.observe(element);
			}
		});
		
		// Add scroll listener for more responsive updates
		window.addEventListener('scroll', handleScroll, { passive: true });
		
		// Initial check
		handleScroll();
		
		return () => {
			observer.disconnect();
			window.removeEventListener('scroll', handleScroll);
		};
	});
	
	function handleAnchorClick(event: MouseEvent, anchor: string) {
		event.preventDefault();
		const element = document.getElementById(anchor);
		if (element) {
			// Calculate offset for fixed header
			const offset = 80;
			const elementPosition = element.getBoundingClientRect().top + window.scrollY;
			const offsetPosition = elementPosition - offset;
			
			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth'
			});
			
			// Update active anchor immediately
			activeAnchor = anchor;
			
			// Update URL hash
			if (window.history.pushState) {
				window.history.pushState(null, '', `#${anchor}`);
			}
		}
	}
</script>

<aside class="toc" bind:this={tocElement}>
	<div class="toc__header">
		<h3 class="toc__title">ON THIS PAGE</h3>
	</div>
	
	<nav class="toc__nav" aria-label="Table of contents">
		<ul class="toc__list">
			{#each items as item}
				<li class="toc__item toc__item--level-{item.level}">
					<button
						class="toc__link"
						class:toc__link--active={activeAnchor === item.anchor}
						onclick={(e) => handleAnchorClick(e, item.anchor)}
					>
						<span class="toc__link-indicator"></span>
						<span class="toc__link-text">{item.title}</span>
					</button>
				</li>
			{/each}
		</ul>
	</nav>
</aside>

<style>
	.toc {
		position: sticky;
		top: calc(var(--header-height, 52px) + 49px + 2rem); /* Header + topbar + gap */
		align-self: flex-start;
		background: transparent;
		border: none;
		border-radius: 0;
		padding: 0;
		width: 240px;
		max-width: 240px;
		max-height: calc(100vh - var(--header-height, 52px) - 49px - 4rem);
		overflow-y: auto;
		overflow-x: hidden;
		scrollbar-width: none;
		-ms-overflow-style: none;
		border-left: 1px solid var(--border-light);
		margin-left: 2rem;
	}
	
	.toc::-webkit-scrollbar {
		display: none;
	}
	
	.toc__header {
		margin-bottom: 0.5rem;
		padding: 0 1rem 0 1rem;
		border-bottom: none;
	}
	
	.toc__title {
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--text-tertiary);
		margin: 0;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}
	
	.toc__list {
		list-style: none;
		padding: 0;
		margin: 0;
		position: relative;
	}
	
	.toc__item {
		margin-bottom: 0;
		position: relative;
	}
	
	.toc__item--level-2 {
		margin-left: 0;
	}
	
	.toc__item--level-3 .toc__link {
		padding-left: 1.75rem;
		font-size: 0.75rem;
	}
	
	.toc__item--level-3 .toc__link:not(.toc__link--active) {
		opacity: 0.85;
	}
	
	.toc__item--level-4 .toc__link {
		padding-left: 2.5rem;
		font-size: 0.75rem;
	}
	
	.toc__item--level-4 .toc__link:not(.toc__link--active) {
		opacity: 0.85;
	}
	
	.toc__item--level-5 .toc__link,
	.toc__item--level-6 .toc__link {
		padding-left: 3.25rem;
		font-size: 0.75rem;
	}
	
	.toc__item--level-5 .toc__link:not(.toc__link--active),
	.toc__item--level-6 .toc__link:not(.toc__link--active) {
		opacity: 0.85;
	}
	
	.toc__link {
		display: flex;
		align-items: center;
		width: 100%;
		padding: 0.125rem 1rem;
		border: none;
		background: none;
		text-align: left;
		font-size: 0.8125rem;
		line-height: 1.5;
		color: var(--text-tertiary);
		text-decoration: none;
		border-radius: 0;
		cursor: pointer;
		transition: color 0.15s ease;
		position: relative;
	}
	
	.toc__link-indicator {
		position: absolute;
		left: -1px;
		top: 0;
		bottom: 0;
		width: 2px;
		background: var(--brand-primary);
		opacity: 0;
		transition: opacity 0.15s ease;
	}
	
	.toc__link-text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 100%;
	}
	
	.toc__link:hover {
		color: var(--text-secondary);
		opacity: 1;
	}
	
	.toc__link:hover .toc__link-text {
		transform: none;
	}
	
	.toc__link--active {
		color: var(--text-primary);
		font-weight: 500;
		opacity: 1;
	}
	
	.toc__link--active .toc__link-indicator {
		background: var(--brand-primary);
		opacity: 1;
	}
	
	

	/* Hide on mobile and tablet */
	@media (max-width: 1200px) {
		.toc {
			display: none;
		}
	}
</style>